// WeatherDataWidgetsDeck — weather station map pins rendered as deck.gl layers (experiment: avoids DOM marker pan wobble)
import { MapboxOverlay } from '@deck.gl/mapbox';
import { IconLayer, TextLayer } from '@deck.gl/layers';
import chroma from 'chroma-js';
import { createDefaultWeatherService } from '../weather/WeatherService.js';
import { registerClickInterceptor, unregisterClickInterceptor } from '../ui-map/map.js';
import * as util from '../utils/utils.js';
import { unwrapAngle } from '../utils/geometry-util.js';

const PIN_RADIUS_PX = 13;
const NO_DATA_RADIUS_PX = 4;
const ON_DEMAND_FETCH_RADIUS_M = 10;
const DEG_TO_M = 111195;
const DECLUTTER_MIN_DIST_PX = 60;
const MIN_ZOOM_FOR_HIDDEN_PINS = 10;
const ARROW_TIP_LEN_PX = 10; // length of the point sticking out beyond the circle's top edge
const ARROW_WIDTH_PX = 23;

// single combined circle+arrow icon per station, drawn tip-up (0deg); anchor sits at the
// circle's center so getAngle rotation pivots the arrow around the circle edge while the
// (rotationally symmetric) circle itself looks unchanged.
// The arrow's base is set at the height where the circle's chord width equals the arrow
// width, so its two base corners land exactly on the circle's circumference (matching the
// original single-shape arrow icon, where the base corners intersected the circle).
const ICON_W = Math.max(ARROW_WIDTH_PX, PIN_RADIUS_PX * 2);
const ICON_H = ARROW_TIP_LEN_PX + PIN_RADIUS_PX * 2;
const ANCHOR_X = ICON_W / 2;
const ANCHOR_Y = ARROW_TIP_LEN_PX + PIN_RADIUS_PX;
const BASE_HALF_WIDTH = Math.min(ARROW_WIDTH_PX / 2, PIN_RADIUS_PX);
const BASE_Y = ANCHOR_Y - Math.sqrt(PIN_RADIUS_PX ** 2 - BASE_HALF_WIDTH ** 2);

const ICON_MAPPING = {
  arrow: { x: 0, y: 0, width: ICON_W, height: ICON_H, anchorX: ANCHOR_X, anchorY: ANCHOR_Y, mask: true },
  plain: { x: ICON_W, y: 0, width: ICON_W, height: ICON_H, anchorX: ANCHOR_X, anchorY: ANCHOR_Y, mask: true }
};

function toDeckColor(colorStr) {
  const c = chroma(colorStr);
  const [r, g, b] = c.rgb();
  return [r, g, b, 120];
}

function distancePx(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function drawCircle(ctx) {
  ctx.beginPath();
  ctx.arc(ANCHOR_X, ANCHOR_Y, PIN_RADIUS_PX, 0, Math.PI * 2);
  ctx.fill();
}

function buildIconAtlas() {
  const canvas = document.createElement('canvas');
  canvas.width = ICON_W * 2;
  canvas.height = ICON_H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#000';

  drawCircle(ctx);
  ctx.beginPath();
  ctx.moveTo(ANCHOR_X, 0);
  ctx.lineTo(ANCHOR_X + BASE_HALF_WIDTH, BASE_Y);
  ctx.lineTo(ANCHOR_X - BASE_HALF_WIDTH, BASE_Y);
  ctx.closePath();
  ctx.fill();

  ctx.save();
  ctx.translate(ICON_W, 0);
  drawCircle(ctx);
  ctx.restore();

  return canvas;
}

const ICON_ATLAS = buildIconAtlas();
const TRANSITIONS_SUPPORTED = util.supportsGpuAttributeTransitions();
const REDRAW_INTERVAL_MS = 1000;
const GREY_COLOR = toDeckColor('#888');

export class WeatherDataWidgetsDeck {

  #map;
  #onWeatherLoad;
  #service;
  #statusEl = null;
  #stations = [];
  #visible  = true;
  #currentT = null;
  #popup    = null;
  #canvas   = null;
  #overlay  = null;
  #extent   = null;
  #declutterHidden = new Set();
  #clickInterceptor;
  #onZoomEnd;
  #onZoom;
  #terrainMode = false;
  #redrawTimer = null;
  #redrawPending = false;
  #lastAngle = new Map();

  constructor(map, extent, onWeatherLoad) {
    this.#map = map;
    this.#extent = extent;
    this.#onWeatherLoad = onWeatherLoad;
    this.#service = createDefaultWeatherService();
    this.#overlay = new MapboxOverlay({
      layers: [],
      onHover: info => this.#handleHover(info)
    });
    this.#map.addControl(this.#overlay);
    this.#clickInterceptor = evt => this.#tryHandleClick(evt);
    registerClickInterceptor(this.#clickInterceptor);
    this.#onZoomEnd = () => this.#recomputeDeclutter();
    this.#map.on('zoomend', this.#onZoomEnd);
    this.#onZoom = () => this.#redraw();
    this.#map.on('zoom', this.#onZoom);
  }

  init(statusEl) {
    this.#statusEl = statusEl;
  }

  destroy() {
    clearTimeout(this.#redrawTimer);
    this.#map.off('zoomend', this.#onZoomEnd);
    this.#map.off('zoom', this.#onZoom);
    unregisterClickInterceptor(this.#clickInterceptor);
    this.#map.removeControl(this.#overlay);
    this.#popup?.remove();
  }

  loadStations(stations) {
    this.#stations = stations;
    this.#redraw();
  }

  setVisible(visible) {
    this.#visible = visible;
    this.#redraw();
  }

  setTerrainMode(terrainMode) {
    this.#terrainMode = terrainMode;
    this.#redraw();
  }

  onTimeUpdate(ts) {
    this.#currentT = ts;
    if (!this.#stations.length) return;
    this.#redraw();
  }

  async fetchWeather(extent, notify = false) {
    if (!extent) return;
    // this.#extent = extent;
    this.#setStatus('Fetching weather…');
    try {
      const stations = await this.#service.fetchForRace(extent);
      this.#stations = stations;
      this.#onWeatherLoad(stations);
      this.#redraw();
      this.#setStatus(`${stations.length} station(s)`);
      if (notify)
        util.showToast(`Got weather data for ${stations.length} stations.`)
    } catch (e) {
      this.#setStatus(`Weather fetch failed: ${e.message}`);
      console.warn('WeatherDataWidgetsDeck fetch error:', e);
      util.notifyError("Failed to fetch weather data", e);
    }
  }

  #buildPinData() {
    const t = this.#currentT ?? Date.now();
    const zoom = this.#map.getZoom();
    return this.#stations
      .filter(station => !this.#declutterHidden.has(station.id))
      .map(station => {
        const obs = station.obsAt(t);
        const hidden = station.observations.length === 0 || station.isHidden();
        const angle = unwrapAngle(this.#lastAngle.get(station.id), (obs?.dd ?? 0) + 180);
        this.#lastAngle.set(station.id, angle);
        return {
          station,
          obs,
          hidden,
          position: [station.lon, station.lat],
          angle,
          //color: TRANSITIONS_SUPPORTED ? toDeckColor(hidden ? '#888' : (obs?.color ?? '#888')) : GREY_COLOR
          color: toDeckColor(hidden ? '#888' : (obs?.color ?? '#888')),
          //color: GREY_COLOR
        };
      })
      .filter(d => !d.hidden || zoom >= MIN_ZOOM_FOR_HIDDEN_PINS);
  }

  #recomputeDeclutter() {
    console.log(this.#map.getZoom());
    const candidates = this.#stations
      .filter(station => !station.isHidden())
      .map(station => ({ station, screen: this.#map.project([station.lon, station.lat]) }))
      .sort((a, b) => a.station.score - b.station.score);

    const declutterHidden = new Set();
    const kept = [];
    for (const candidate of candidates) {
      const collides = kept.some(k => distancePx(k.screen, candidate.screen) < DECLUTTER_MIN_DIST_PX);
      if (collides) declutterHidden.add(candidate.station.id);
      else kept.push(candidate);
    }
    this.#declutterHidden = declutterHidden;
    this.#redraw();
  }

  #tryHandleClick(evt) {
    if (!this.#overlay || !this.#visible) return false;
    const picked = this.#overlay.pickObject({ x: evt.point.x, y: evt.point.y });
    if (!picked?.object) return false;
    this.#handleClick(picked.object.station);
    return true;
  }

  #handleClick(station) {
    const currentlyHidden = station.observations.length === 0 || station.isHidden();
    if (currentlyHidden) {
      station.setHidden(false);
      if (station.observations.length === 0) this.#fetchStationOnDemand(station);
    } else {
      station.setHidden(true);
    }
    this.#onWeatherLoad(this.#stations);
    this.#redraw();
  }

  async #fetchStationOnDemand(station) {
    if (!this.#extent) return;
    const dLat = ON_DEMAND_FETCH_RADIUS_M / DEG_TO_M;
    const dLon = ON_DEMAND_FETCH_RADIUS_M / (DEG_TO_M * Math.cos(station.lat * Math.PI / 180));
    const bbox = {
      minLat: station.lat - dLat, maxLat: station.lat + dLat,
      minLon: station.lon - dLon, maxLon: station.lon + dLon
    };
    console.log('fetch station:', station.name, new Date(this.#extent.startMs), new Date(this.#extent.endMs), bbox);
    try {
      const results = await this.#service.fetch(this.#extent.startMs, this.#extent.endMs, bbox, Date.now(), [], true);
      if (!results.length) {
        console.log("Got no results for:"+station.name);
        return;
      }
      for (const result of results) {
        const idx = this.#stations.findIndex(s => s.sameStation(result));
        if (idx >= 0) this.#stations[idx] = result;
        else this.#stations.push(result);
      }
      this.#onWeatherLoad(this.#stations);
      this.#redraw();
    } catch (e) {
      console.warn('WeatherDataWidgetsDeck on-demand fetch error:', e);
      util.notifyError(`Failed to fetch weather data for ${station.name}`, e);
    }
  }

  #redraw() {
    if (this.#redrawTimer) {
      this.#redrawPending = true;
      return;
    }
    this.#doRedraw();
    this.#redrawTimer = setTimeout(() => {
      this.#redrawTimer = null;
      if (this.#redrawPending) {
        this.#redrawPending = false;
        this.#redraw();
      }
    }, REDRAW_INTERVAL_MS);
  }

  #doRedraw() {
    if (!this.#overlay) return;
    if (!this.#visible || !this.#stations.length || this.#terrainMode) {
      this.#overlay.setProps({ layers: [] });
      return;
    }

    const data = this.#buildPinData();
    const speedData = data.filter(d => d.obs?.ff != null && !d.hidden);

    const layers = [
      new IconLayer({
        id: 'wx-pin',
        data,
        pickable: true,
        iconAtlas: ICON_ATLAS,
        iconMapping: ICON_MAPPING,
        sizeUnits: 'pixels',
       // transitions: TRANSITIONS_SUPPORTED ? { getAngle: 300, getColor: 300 } : undefined,
       transitions: TRANSITIONS_SUPPORTED ? { getAngle: 300 } : undefined,
        getPosition: d => d.position,
        getIcon: d => !d.hidden && d.obs?.dd != null ? 'arrow' : 'plain',
        getSize: d => d.hidden ? ICON_H * NO_DATA_RADIUS_PX / PIN_RADIUS_PX : ICON_H,
        getAngle: d => d.angle,
        getColor: d => d.color
      }),
      new TextLayer({
        id: 'wx-pin-speed',
        data: speedData,
        pickable: false,
        getPosition: d => d.position,
        getText: d => `${Math.round(d.obs.ff)}`,
        getSize: 14,
        fontWeight: 'bold',
        getColor: [255, 255, 255, 255],
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center'
      })
    ];

    this.#overlay.setProps({ layers });
  }

  #handleHover(info) {
    this.#map.getCanvas().style.cursor = info.object ? 'pointer' : '';
    if (info.object) {
      this.#showPopup(info.object.station, info.x, info.y);
    } else if (this.#popup) {
      this.#popup.style.display = 'none';
    }
  }

  #showPopup(station, x, y) {
    if (!this.#popup) this.#popup = this.#createPopup();
    this.#popup.querySelector('.wx-popup-label').textContent = `${station.name}  ·  ${station.source}`;
    if (station.observations.length === 0) {
      const ctx = this.#canvas.getContext('2d');
      ctx.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, this.#canvas.width, this.#canvas.height);
      ctx.fillStyle = '#888';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No data', this.#canvas.width / 2, this.#canvas.height / 2);
    } else {
      drawWindGraph(this.#canvas, station, this.#currentT ?? Date.now());
    }
    const rect = this.#map.getContainer().getBoundingClientRect();
    const pw = this.#popup.offsetWidth || 400, ph = this.#popup.offsetHeight || 140;
    let left = rect.left + x + 12, top = rect.top + y - ph - 8;
    if (left + pw > window.innerWidth) left = rect.left + x - pw - 12;
    if (top < 0) top = rect.top + y + 12;
    this.#popup.style.left = left + 'px';
    this.#popup.style.top  = top + 'px';
    this.#popup.style.display = 'block';
  }

  #createPopup() {
    const popup = document.createElement('div');
    popup.className = 'wx-popup';
    popup.style.display = 'none';
    popup.innerHTML = '<div class="wx-popup-label"></div><canvas width="400" height="140"></canvas>';
    this.#canvas = popup.querySelector('canvas');
    document.body.appendChild(popup);
    return popup;
  }

  #setStatus(msg) {
    if (this.#statusEl) this.#statusEl.textContent = msg;
  }
}

export function drawWindGraph(canvas, station, t) {
  const W = canvas.width, H = canvas.height;
  const ML = 20, MR = 4, MT = 5, MB = 13;
  const PW = W - ML - MR, PH = H - MT - MB;
  const WINDOW_MS = 6 * 60 * 60 * 1000;
  const HALF_HOUR = 30 * 60 * 1000;

  const startT = t - WINDOW_MS / 2;
  const endT   = t + WINDOW_MS / 2;

  const windowObs = station.observations.filter(o => o.t >= startT && o.t <= endT && o.ff != null);
  const peakKn = windowObs.length ? Math.max(...windowObs.map(o => o.ff)) : 20;
  const MAX_KN = Math.ceil((peakKn + 10) / 5) * 5;

  const tx = ms => ML + (ms - startT) / WINDOW_MS * PW;
  const sy = kn => MT + PH - Math.min(kn / MAX_KN, 1) * PH;

  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#f5f5f5';
  ctx.fillRect(0, 0, W, H);

  ctx.font = '8px sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (let kn = 5; kn < MAX_KN; kn += 5) {
    const y = sy(kn);
    ctx.strokeStyle = kn % 10 === 0 ? '#ccc' : '#e4e4e4';
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(ML, y); ctx.lineTo(W - MR, y); ctx.stroke();
    ctx.fillStyle = '#222';
    ctx.fillText(kn, ML - 3, y);
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  let tick = Math.ceil(startT / HALF_HOUR) * HALF_HOUR;
  while (tick <= endT) {
    const x = tx(tick);
    const isHour = new Date(tick).getMinutes() === 0;
    ctx.strokeStyle = isHour ? '#aaa' : '#ddd';
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(x, MT); ctx.lineTo(x, MT + PH + 3); ctx.stroke();
    if (isHour) {
      const d = new Date(tick);
      ctx.fillStyle = '#222';
      ctx.fillText(`${String(d.getHours()).padStart(2, '0')}:00`, x, MT + PH + 4);
    }
    tick += HALF_HOUR;
  }

  ctx.strokeStyle = '#999';
  ctx.lineWidth = 0.5;
  ctx.strokeRect(ML, MT, PW, PH);

  const nowX = tx(t);
  ctx.strokeStyle = 'rgba(255,60,60,0.5)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(nowX, MT); ctx.lineTo(nowX, MT + PH); ctx.stroke();

  const obs = station.observations.filter(o => o.t >= startT && o.t <= endT);
  if (!obs.length) return;
  ctx.strokeStyle = '#4af';
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  let started = false;
  for (const o of obs) {
    if (o.ff == null) { started = false; continue; }
    if (!started) { ctx.moveTo(tx(o.t), sy(o.ff)); started = true; }
    else ctx.lineTo(tx(o.t), sy(o.ff));
  }
  ctx.stroke();

  tick = Math.ceil(startT / HALF_HOUR) * HALF_HOUR;
  while (tick <= endT) {
    let nearest = null, minDt = 20 * 60 * 1000;
    for (const o of station.observations) {
      const dt = Math.abs(o.t - tick);
      if (dt < minDt && o.dd != null) { minDt = dt; nearest = o; }
    }
    if (nearest) drawArrow(ctx, tx(tick), (sy(MAX_KN) + sy(MAX_KN - 5)) / 2, nearest.dd);
    tick += HALF_HOUR;
  }
}

function drawArrow(ctx, cx, cy, deg) {
  const R = 4.5;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((deg + 180) * Math.PI / 180);
  ctx.fillStyle = 'rgba(0,0,0,0.75)';
  ctx.beginPath();
  ctx.moveTo(0, -R);
  ctx.lineTo(R * 0.55, R * 0.65);
  ctx.lineTo(0, R * 0.2);
  ctx.lineTo(-R * 0.55, R * 0.65);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}
